import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Frilys cart-drawer upsell (snippets/elevo-cart-upsell.liquid).
 *
 * The card decides nothing on the client: Liquid picks the offer from the fresh cart on every render.
 * This element only performs the click with one request — POST /cart/update.js with the variant map
 * ({ remove: 0, add: 1 } for a ladder swap, { add: 1 } for a companion) and `sections=cart-drawer-section` —
 * then resolves the theme's standard CartLinesUpdateEvent so <cart-items-component> morphs the drawer
 * (items, summary and this card) exactly like a product-form add would.
 */
class ElevoCartUpsell extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-upsell-action]') : null;
    if (!(button instanceof HTMLButtonElement) || this.#busy) return;

    const { upsellAction, add, remove } = button.dataset;
    if (!add) return;

    this.#busy = true;
    button.disabled = true;
    this.setAttribute('aria-busy', 'true');

    /** @type {Record<string, number>} */
    const updates = {};
    if (upsellAction === 'swap' && remove) updates[remove] = 0;
    updates[add] = 1;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: upsellAction === 'swap' ? 'change' : 'add',
        context: 'cart',
        lines: [{ merchandiseId: add, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const reset = () => {
      this.#busy = false;
      button.disabled = false;
      this.removeAttribute('aria-busy');
    };

    fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: 'cart-drawer-section' }),
    })
      .then((response) => response.json())
      .then((cart) => {
        if (cart.status) throw new Error(cart.message || cart.description || 'Cart update failed');
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            sections: cart.sections,
            source: 'elevo-cart-upsell',
            didError: false,
          },
        });
        // The morph usually replaces this card; if it is kept, be ready for the next click.
        reset();
      })
      .catch((error) => {
        console.warn('[elevo-cart-upsell]', error);
        deferred.reject(error);
        reset();
      });
  };
}

if (!customElements.get('elevo-cart-upsell')) {
  customElements.define('elevo-cart-upsell', ElevoCartUpsell);
}
